import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, BellOff, X } from "lucide-react";
import { toast } from "sonner";

const LS_KEY = "lc_reminder";

function scheduleTimer(time, onFire) {
  if (!time) return null;
  const [hh, mm] = time.split(":").map(Number);
  const now = new Date();
  const next = new Date();
  next.setHours(hh, mm, 0, 0);
  if (next <= now) next.setDate(next.getDate() + 1);
  return setTimeout(onFire, next - now);
}

export function useReminders() {
  useEffect(() => {
    let timer;
    const setup = () => {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const { time, enabled } = JSON.parse(raw);
      if (!enabled || !time) return;
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      timer = scheduleTimer(time, () => {
        new Notification("MyLymphieLife ♡", {
          body: "Gentle nudge — take a moment to log today.",
          icon: "/favicon.ico",
        });
        setup(); // schedule tomorrow
      });
    };
    setup();
    return () => timer && clearTimeout(timer);
  }, []);
}

export default function ReminderSettings({ onClose }) {
  const [time, setTime] = useState("20:00");
  const [enabled, setEnabled] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");

  useEffect(() => {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setTime(p.time || "20:00");
        setEnabled(!!p.enabled);
      } catch {}
    }
  }, []);

  const requestPerm = async () => {
    if (!("Notification" in window)) return toast.error("Notifications not supported in this browser");
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === "granted") toast.success("Notifications enabled ♡");
    else toast.error("Permission denied");
  };

  const save = () => {
    if (enabled && perm !== "granted") {
      toast.error("Please allow notifications first");
      return;
    }
    localStorage.setItem(LS_KEY, JSON.stringify({ time, enabled }));
    toast.success(enabled ? `Reminder set for ${time} daily ♡` : "Reminder turned off");
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 backdrop-blur-xl bg-white/40 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md kawaii-card p-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Reminders</p>
            <h2 className="font-heading text-2xl font-semibold text-kawaii-text mt-1">Gentle daily nudge</h2>
          </div>
          <button
            onClick={onClose} data-testid="close-reminder-btn"
            className="kawaii-btn w-10 h-10 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        <p className="text-sm text-kawaii-textSoft font-body mb-6">
          A soft daily reminder to write your entry. Runs locally in this browser — nothing is sent anywhere.
        </p>

        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => setEnabled(!enabled)}
            data-testid="toggle-reminder"
            className={`kawaii-btn w-14 h-14 rounded-2xl flex items-center justify-center border ${enabled ? 'bg-kawaii-pink text-white border-kawaii-pink' : 'bg-white text-kawaii-textSoft border-kawaii-border'}`}
          >
            {enabled ? <Bell className="w-5 h-5" strokeWidth={2.5} /> : <BellOff className="w-5 h-5" strokeWidth={2.5} />}
          </button>
          <div>
            <div className="font-heading font-semibold text-kawaii-text">{enabled ? "Reminders on" : "Reminders off"}</div>
            <div className="text-xs text-kawaii-textSoft">Tap to toggle</div>
          </div>
        </div>

        <div className="mb-5">
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Time of day</label>
          <input
            type="time" value={time} onChange={(e) => setTime(e.target.value)}
            data-testid="reminder-time"
            className="mt-2 w-full px-4 py-3 rounded-2xl border border-kawaii-border bg-white/70 font-body text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
          />
        </div>

        {perm !== "granted" && (
          <button
            onClick={requestPerm}
            data-testid="request-perm-btn"
            className="kawaii-btn w-full py-3 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text mb-3"
          >
            Enable browser notifications
          </button>
        )}

        <button
          onClick={save}
          data-testid="save-reminder-btn"
          className="kawaii-btn w-full py-3 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-heading font-semibold shadow-md"
        >
          Save
        </button>
      </motion.div>
    </motion.div>
  );
}
